import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, RefreshCw, Trash2 } from 'lucide-react';
import type { PrototypeCustomDomainDto } from '@aiai/shared';
import {
  addPrototypeDomain,
  deletePrototypeDomain,
  verifyPrototypeDomain,
} from '../api';
import { invalidatePrototype, updatePrototypeCache } from '../api/prototypeCache';
import { errorMessage } from '../lib/api';
import { Badge, type Tone } from './Badge';
import { Callout } from './Callout';
import { SectionCard } from './SectionCard';
import { Spinner } from './Spinner';

const STATUS_TONE: Record<PrototypeCustomDomainDto['status'], Tone> = {
  pending: 'warning',
  active: 'success',
  failed: 'danger',
};

/**
 * Card to point a custom domain at this prototype. A domain only
 * goes live (and gets its Let's Encrypt cert) after Verify passes the DNS
 * check — Verify doubles as the retry for failed checks or cert issuance.
 */
export function CustomDomainsCard({
  prototypeId,
  cnameTarget,
  domains,
}: {
  prototypeId: string;
  cnameTarget: string;
  domains: PrototypeCustomDomainDto[];
}) {
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState('');
  // Row whose last Verify passed the DNS check — the cert follows async.
  const [verifiedId, setVerifiedId] = useState<string | null>(null);

  // Patch the cached prototype right away (the page may key it by code,
  // not id — see prototypeCache), then refetch for the server's view.
  const applyDomains = (
    update: (list: PrototypeCustomDomainDto[]) => PrototypeCustomDomainDto[],
  ) => {
    updatePrototypeCache(queryClient, prototypeId, (proto) => ({
      ...proto,
      deployment: { ...proto.deployment, domains: update(proto.deployment.domains ?? []) },
    }));
    invalidatePrototype(queryClient, prototypeId);
  };

  const addMutation = useMutation({
    mutationFn: (value: string) => addPrototypeDomain(prototypeId, value),
    onSuccess: (added) => {
      setDomain('');
      applyDomains((list) => [...list.filter((d) => d.id !== added.id), added]);
    },
  });
  const verifyMutation = useMutation({
    mutationFn: (domainId: string) => verifyPrototypeDomain(prototypeId, domainId),
    onMutate: () => setVerifiedId(null),
    onSuccess: (checked) => {
      setVerifiedId(checked.status === 'failed' ? null : checked.id);
      applyDomains((list) => list.map((d) => (d.id === checked.id ? checked : d)));
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (domainId: string) => deletePrototypeDomain(prototypeId, domainId),
    onSuccess: (_, domainId) => applyDomains((list) => list.filter((d) => d.id !== domainId)),
  });

  const actionError = addMutation.error ?? verifyMutation.error ?? deleteMutation.error;

  return (
    <SectionCard title="Custom domains" count={domains.length}>
      <div className="space-y-4">
        <p className="text-xs text-ink-muted">
          Point the client&apos;s domain at{' '}
          <code className="rounded bg-surface-3 px-1.5 py-0.5 font-mono text-ink">
            {cnameTarget}
          </code>{' '}
          with a CNAME record, then hit Verify. Certificates are issued
          automatically and appear within about a minute. Apex domains
          can&apos;t CNAME — use the www subdomain, or an A/ALIAS record to
          this server&apos;s IP.
        </p>

        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const value = domain.trim();
            if (value) addMutation.mutate(value);
          }}
        >
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="www.client-domain.com"
            className="input flex-1 font-mono"
            aria-label="Custom domain"
          />
          <button
            type="submit"
            className="btn-secondary"
            disabled={!domain.trim() || addMutation.isPending}
          >
            {addMutation.isPending ? <Spinner className="h-3.5 w-3.5" /> : 'Add'}
          </button>
        </form>

        {actionError != null && (
          <Callout tone="danger">{errorMessage(actionError, 'Domain action failed')}</Callout>
        )}

        {domains.length === 0 ? (
          <p className="text-xs text-ink-faint">No custom domains yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {domains.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-3 py-2.5">
                <Globe className="h-3.5 w-3.5 text-ink-faint" aria-hidden="true" />
                <span className="font-mono text-sm text-ink">{d.domain}</span>
                <Badge tone={STATUS_TONE[d.status]} size="sm">
                  {d.status}
                </Badge>
                {d.status === 'failed' && d.lastError && (
                  <span className="basis-full text-xs text-danger-300 sm:basis-auto">
                    {d.lastError}
                  </span>
                )}
                {verifiedId === d.id && d.status !== 'failed' && (
                  <span
                    role="status"
                    className="basis-full text-xs text-success-300 sm:basis-auto"
                  >
                    DNS OK — applying certificate, this can take about a minute
                  </span>
                )}
                <span className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => verifyMutation.mutate(d.id)}
                    disabled={verifyMutation.isPending && verifyMutation.variables === d.id}
                    title="Check DNS and apply the domain — also retries a failed certificate"
                  >
                    {verifyMutation.isPending && verifyMutation.variables === d.id ? (
                      <Spinner className="h-3.5 w-3.5" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    Verify
                  </button>
                  <button
                    type="button"
                    className="btn-danger-ghost btn-sm"
                    onClick={() => deleteMutation.mutate(d.id)}
                    disabled={deleteMutation.isPending}
                    title="Remove the domain and its router"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Remove
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </SectionCard>
  );
}
